import { clientPortalApi } from '@/lib/api/client-portal.api'
import { notificationsApi } from '@/lib/api/notifications.api'

import type { BulkUpdateNotificationPreferencesDto } from '@scrambl/shared'
import {
  CLIENT_PORTAL_EMAIL_NOTIFICATION_TYPES,
  CLIENT_PORTAL_NOTIFICATION_TYPES,
  isValidUUID,
  NotificationChannel,
  NotificationType,
} from '@scrambl/shared'

import type { NotificationReaderContext } from './reads.api'
import {
  assertNotificationPreference,
  confirmNotificationPreferenceReset,
  confirmNotificationPreferences,
  NotificationCommandContractError,
  notificationPreferenceBatchInput,
} from './commands.api'
import {
  assertNotificationReaderContext,
  NotificationReadContractError,
} from './reads.api'

function assertPortal(entityId: string | null) {
  if (entityId !== null && !isValidUUID(entityId))
    throw new NotificationCommandContractError()
}
function portalReadChoice(
  channel: NotificationChannel,
  type: NotificationType,
) {
  return (
    (channel === NotificationChannel.IN_APP ||
      channel === NotificationChannel.EMAIL) &&
    (
      [
        ...CLIENT_PORTAL_NOTIFICATION_TYPES,
        NotificationType.TICKET_ASSIGNED,
      ] as readonly NotificationType[]
    ).includes(type)
  )
}
function portalChoice(channel: NotificationChannel, type: NotificationType) {
  return (
    portalReadChoice(channel, type) &&
    (channel !== NotificationChannel.EMAIL ||
      CLIENT_PORTAL_EMAIL_NOTIFICATION_TYPES.includes(type))
  )
}
export const deliveryPreferencesApi = {
  async read(
    context: NotificationReaderContext,
    portalEntityId: string | null,
  ) {
    assertNotificationReaderContext(context)
    assertPortal(portalEntityId)
    const rows = portalEntityId
      ? await clientPortalApi.getNotificationPreferences(
          portalEntityId,
          context,
        )
      : await notificationsApi.getPreferences(context)
    assertNotificationReaderContext(context)
    try {
      if (!Array.isArray(rows)) throw new NotificationReadContractError()
      const seen = new Set<string>()
      for (const row of rows) {
        assertNotificationPreference(row, context)
        const key = JSON.stringify([row.channel, row.type])
        if (
          seen.has(key) ||
          (portalEntityId && !portalReadChoice(row.channel, row.type))
        )
          throw new NotificationReadContractError()
        seen.add(key)
      }
    } catch {
      throw new NotificationReadContractError()
    }
    return rows
  },
  async save(
    input: BulkUpdateNotificationPreferencesDto,
    context: NotificationReaderContext,
    portalEntityId: string | null,
  ) {
    assertNotificationReaderContext(context)
    assertPortal(portalEntityId)
    const parsed = notificationPreferenceBatchInput.safeParse(input)
    if (
      !parsed.success ||
      new Set(
        parsed.data.preferences.map(p => JSON.stringify([p.channel, p.type])),
      ).size !== parsed.data.preferences.length ||
      (portalEntityId &&
        parsed.data.preferences.some(p => !portalChoice(p.channel, p.type)))
    )
      throw new NotificationCommandContractError()
    const reply = portalEntityId
      ? await clientPortalApi.updateNotificationPreferences(
          portalEntityId,
          parsed.data,
          context,
        )
      : await notificationsApi.updateMultiplePreferences(parsed.data, context)
    return confirmNotificationPreferences(
      reply,
      context,
      parsed.data.preferences,
    )
  },
  async reset(
    context: NotificationReaderContext,
    portalEntityId: string | null,
  ) {
    assertNotificationReaderContext(context)
    if (portalEntityId !== null) throw new NotificationCommandContractError()
    return confirmNotificationPreferenceReset(
      await notificationsApi.setDefaultPreferences(context),
      context,
    )
  },
}
